import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, MapPin, Users, CheckCircle2, Building2, Shirt } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15c-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";

const TSHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

export default function CorporateEmployeeRegistration() {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    liability_waiver: false,
    photo_release: false,
    tshirt_size: '',
    dietary_restrictions: ''
  });

  useEffect(() => {
    fetchEvent();
  }, [token]);

  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API}/corporate/events/register/${token}`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
      } else {
        toast.error('Event not found or link has expired');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!formData.liability_waiver) {
      toast.error('You must accept the liability waiver to register');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/corporate/volunteers/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: event.event_id,
          ...formData
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Successfully registered!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to register');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <h1 className="text-2xl font-bold text-[#1a3a5c] mb-4">Event Not Found</h1>
            <p className="text-slate-600 mb-6">This registration link may have expired or is invalid.</p>
            <Link to="/">
              <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full">
                Go to Home Page
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="registration-success">
        <header className="bg-white shadow-sm">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <img src={LOGO_URL} alt="The Bookshelf Project" className="h-10 w-auto" />
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full border-0 shadow-xl">
            <CardContent className="p-8 text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-[#1a3a5c] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  You're Registered!
                </h1>
                <p className="text-slate-600">
                  Thank you for signing up for the {event.company_name} volunteer day.
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6 text-left space-y-3">
                <div className="flex items-center gap-3 text-slate-700">
                  <Calendar className="w-5 h-5 text-[#5cc8e8]" />
                  <span>{new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Clock className="w-5 h-5 text-[#5cc8e8]" />
                  <span>{event.start_time} - {event.end_time}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <MapPin className="w-5 h-5 text-[#5cc8e8]" />
                  <span>{event.location}</span>
                </div>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-left">
                <p className="text-sm text-amber-800">
                  <strong>What to Wear:</strong> {event.what_to_wear}
                </p>
                <p className="text-sm text-amber-800 mt-2">
                  <strong>What We Provide:</strong> {event.what_we_provide}
                </p>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="employee-registration">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <img src={LOGO_URL} alt="The Bookshelf Project" className="h-10 w-auto" />
        </div>
      </header>

      {/* Event Info Banner */}
      <div className="bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c] text-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-6 h-6" />
            <span className="text-white/80">{event.company_name}</span>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {event.title}
          </h1>
          <p className="text-white/80 mb-6">{event.description}</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#5cc8e8]" />
              <span>{new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#5cc8e8]" />
              <span>{event.start_time} - {event.end_time}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#5cc8e8]" />
              <span>{event.spots_remaining} spots remaining</span>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Form */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {event.spots_remaining <= 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-[#1a3a5c] mb-4">Event is Full</h2>
              <p className="text-slate-600">This volunteer event has reached capacity. Please contact your team coordinator for more information.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl font-bold text-[#1a3a5c] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Employee Registration
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Employee Info */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                      placeholder="John Doe"
                      required
                      data-testid="employee-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData('email', e.target.value)}
                      placeholder="john.doe@company.com"
                      required
                      data-testid="employee-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateFormData('phone', e.target.value)}
                      placeholder="(202) 555-0123"
                      required
                      data-testid="employee-phone-input"
                    />
                  </div>
                </div>

                {/* Waivers */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-semibold text-[#1a3a5c]">Waivers</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="liability_waiver"
                        checked={formData.liability_waiver}
                        onCheckedChange={(checked) => updateFormData('liability_waiver', checked)}
                        data-testid="liability-waiver-checkbox"
                      />
                      <div>
                        <Label htmlFor="liability_waiver" className="cursor-pointer font-medium">
                          Liability Waiver *
                        </Label>
                        <p className="text-xs text-slate-500 mt-1">
                          I release and hold harmless The Bookshelf Project from any claims, damages, or injuries that may occur during volunteer activities.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="photo_release"
                        checked={formData.photo_release}
                        onCheckedChange={(checked) => updateFormData('photo_release', checked)}
                        data-testid="photo-release-checkbox"
                      />
                      <div>
                        <Label htmlFor="photo_release" className="cursor-pointer font-medium">
                          Photo & Media Release
                        </Label>
                        <p className="text-xs text-slate-500 mt-1">
                          I grant permission for photos or videos of me to be used for promotional purposes.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Optional Fields */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1a3a5c]">Optional Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tshirt_size" className="flex items-center gap-2">
                        <Shirt className="w-4 h-4" /> T-Shirt Size
                      </Label>
                      <Select value={formData.tshirt_size} onValueChange={(value) => updateFormData('tshirt_size', value)}>
                        <SelectTrigger data-testid="tshirt-select">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {TSHIRT_SIZES.map((size) => (
                            <SelectItem key={size} value={size}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="dietary_restrictions">Dietary Restrictions</Label>
                      <Input
                        id="dietary_restrictions"
                        value={formData.dietary_restrictions}
                        onChange={(e) => updateFormData('dietary_restrictions', e.target.value)}
                        placeholder="Vegetarian, allergies, etc."
                        data-testid="dietary-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Event Details Reminder */}
                <div className="p-4 bg-amber-50 rounded-xl">
                  <h3 className="font-semibold text-amber-800 mb-2">Event Day Reminders</h3>
                  <p className="text-sm text-amber-700">
                    <strong>What to Wear:</strong> {event.what_to_wear}
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    <strong>What We Provide:</strong> {event.what_we_provide}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full py-6 text-lg"
                  data-testid="register-btn"
                >
                  {submitting ? 'Registering...' : 'Complete Registration'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
